import type { BootstrapResponse, NetBuyResponse, tierDisplaySchema } from "../../../api/schemas";
import type { LiveReads } from "./use-dashboard-reads";
import type { DashboardView, RuntimeStatusView, TierDisplayView } from "../types";
import { actionPermissions, mintDisplayCacheStale, mintPreparationPermission, mintSubmissionPermission } from "../../../state/transaction-capability";
import { formatEth } from "../../../lib/financial";
import { ELYSIUM, nativeSymbol, elysiumHealthReasons, elysiumBootstrapReasons, sameIdentity } from "../../../config/elysium";
import { connectedSourceChainId } from "../../../config/chains";

export function validConfirmedTierDisplay(display: ReturnType<typeof tierDisplaySchema.parse> | undefined): boolean {
  return Boolean(display && display.status === "LAST_CONFIRMED_SNAPSHOT" && display.authorization === false &&
    display.source === "CONFIRMED_DB_SNAPSHOT" && Number.isSafeInteger(display.lagBlocks) &&
    display.lagBlocks !== null && display.lagBlocks !== undefined && display.lagBlocks >= 0 && display.lagBlocks <= 300 &&
    display.asOfBlock !== null && display.asOfBlockHash !== null && display.eligibleTiers !== null);
}

export function normalizeTierDisplay(display: ReturnType<typeof tierDisplaySchema.parse>): TierDisplayView {
  const enhanced = display.source !== undefined || display.lagBlocks !== undefined;
  if (display.status === "UNAVAILABLE" || display.eligibleTiers === null ||
    display.status === "LAST_CONFIRMED_SNAPSHOT" && enhanced && !validConfirmedTierDisplay(display)) {
    return { ...display, status: "UNAVAILABLE", eligibleTiers: null, maxTierUsd: null };
  }
  return { ...display, maxTierUsd: display.eligibleTiers.length === 0 ? null : display.maxTierUsd };
}

export function tierDisplayView(netBuy: NetBuyResponse | null): TierDisplayView {
  const display = netBuy?.tierDisplay;
  if (display) {
    return normalizeTierDisplay(display);
  }
  // Compatibility only for BE versions without tierDisplay. Never infer a snapshot.
  const available = netBuy?.netBuyScope === "CONFIRMED_SNAPSHOT" &&
    netBuy.eligibilityAvailable && netBuy.eligibleTiers !== null;
  return {
    status: available ? "CURRENT" : "UNAVAILABLE",
    eligibleTiers: available ? netBuy.eligibleTiers : null,
    maxTierUsd: available && netBuy.eligibleTiers?.length ? netBuy.maxTierUsd : null,
    asOfBlock: netBuy?.asOfBlock ?? null,
    asOfBlockHash: netBuy?.asOfBlockHash ?? null,
    authorization: false,
    updating: false,
  };
}

export function dashboardView(bootstrap: BootstrapResponse | null, bootstrapError: string | null,
  reads: LiveReads, account: string | null): DashboardView {
  const deploymentError = bootstrapError?.split(",").some(reason =>
    reason.trim().startsWith("ELYSIUM_") || reason.trim().startsWith("CONNECTED_DEPLOYMENT_")) ?? false;
  const displayChainId = bootstrap?.sourceChainId ?? connectedSourceChainId();
  const permissions = actionPermissions(bootstrap, reads.randomness, reads.fee);
  const preparation = mintPreparationPermission(bootstrap, reads.fee);
  const submission = mintSubmissionPermission(bootstrap, reads.fee);
  const cacheStale = mintDisplayCacheStale(reads.fee);
  const feeIssue = deploymentError ? "MINT_FEE_DEPLOYMENT_MISMATCH" : mintFeeDisplayIssue(bootstrap, reads.fee);
  const feeDisplay = reads.fee?.available && !feeIssue ? reads.fee : null;
  // Keep both pool amounts on the last verified snapshot during background reads.
  const poolSnapshot = reads.pool?.poolAvailable && reads.pool.dataStatus === "CURRENT" && reads.pool.availableRewardWei !== null
    ? reads.pool : reads.poolDisplayData;
  const displayPool = poolSnapshot?.poolAvailable && poolSnapshot.dataStatus === "CURRENT" && poolSnapshot.availableRewardWei !== null
    ? poolSnapshot : null;
  const onPrepare = reads.fee?.providerCheckMode === "ON_PREPARE" || reads.randomness?.providerCheckMode === "ON_PREPARE";
  const mainnetMintAvailable = bootstrap?.environment === "production" && submission.state === "ALLOWED" && preparation.state === "ALLOWED";
  const redeemAvailable = permissions.redeem.state === "ALLOWED";
  const finalizeAvailable = permissions.finalize.state === "ALLOWED";
  const environment = bootstrap ? bootstrap.forkTestOnly ? "FORK" : bootstrap.environment === "production" ?
    "PRODUCTION" : "LOCAL" : "READ_ONLY";
  const environmentLabel: DashboardView["environmentLabel"] = environment === "LOCAL" ? "LOCAL TEST" :
    environment === "FORK" ? "FORK TEST" :
      mainnetMintAvailable ? "MAINNET — MINT AVAILABLE" : redeemAvailable ? "MAINNET — REDEEM AVAILABLE" :
        finalizeAvailable ? "MAINNET — TRANSACTIONS AVAILABLE" : "MAINNET — READ ONLY";
  const sourceTokenMissing = Boolean(bootstrap && bootstrap.deployment.sourceTokenStatus === "WAITING_FOR_LAUNCH");
  const projectContractsMissing = Boolean(bootstrap &&
    ["NOT_DEPLOYED", "NOT_CONFIGURED"].includes(bootstrap.deployment.projectContractsStatus));
  const runtime: RuntimeStatusView = {
    nativeSymbol: nativeSymbol(displayChainId),
    permissions,
    deployment: { chainId: bootstrap?.contracts.mintChainId ?? null,
      collection: bootstrap?.contracts.collectionAddress ?? null },
    backend: bootstrapError ? deploymentError ? "DEGRADED" : "OFFLINE" : reads.health?.status === "ok" &&
      (bootstrap?.sourceChainId !== ELYSIUM.chainId || elysiumHealthReasons(reads.health).length === 0) ? "LIVE" : reads.health ? "DEGRADED" :
      reads.status.health.loading ? "LOADING" : "OFFLINE",
    price: reads.netBuy?.priceSource ?? "Unavailable",
    tradeCoverage: reads.netBuy?.eligibilityAvailable ? "CURRENT" : reads.netBuy?.blockingReasons.join(", ") || "Unavailable",
    randomness: bootstrap?.sourceChainId === ELYSIUM.chainId ? "Reward finalizes in the mint transaction" : reads.randomness ? reads.randomness.status === "CHECK_ON_PREPARE" && onPrepare ?
      "Quiver is checked when preparing a mint" : `${reads.randomness.status} · PROVIDER ${reads.randomness.providerApproval}` :
      reads.status.randomness.loading ? "LOADING" : "UNAVAILABLE",
    indexer: reads.randomness?.mirror.freshness.status ?? "UNAVAILABLE",
    readinessReasons: reads.randomness ? [...new Set([
      ...reads.randomness.mirror.freshness.reasons,
      ...(reads.randomness.providerReadiness?.blockingReasons ?? []),
      ...(bootstrap?.sourceChainId !== ELYSIUM.chainId && !onPrepare && reads.randomness.providerApproval === "PENDING" ? ["PROVIDER_PENDING"] : []),
    ])] : reads.status.randomness.error ? ["RANDOMNESS_HEALTH_UNAVAILABLE"] : [],
    configurationNotes: bootstrap ? [
      ...(bootstrap.sourceChainId === ELYSIUM.chainId ? [...elysiumHealthReasons(reads.health), ...preparation.reasons] : []),
      ...(bootstrap.environment === "production" && !bootstrap.capabilities.mainnetWritesEnabled ?
        ["Mainnet transactions are disabled by the backend."] : []),
      ...(bootstrap.sourceChainId !== ELYSIUM.chainId && !bootstrap.contracts.dedicatedFeeReceiverAddress ? ["The backend has not supplied a fee receiver address."] : []),
      ...(typeof window !== "undefined" && (bootstrap.siwe.domain !== window.location.host ||
        new URL(bootstrap.siwe.uri).origin !== window.location.origin) ?
        [`Wallet sign-in is configured for ${bootstrap.siwe.domain}; this page is ${window.location.host}. Confirm the sign-in domain before signing.`] : []),
    ] : deploymentError ? [bootstrapError!] : [],
    publicMiningEnabled: permissions.mining.state === "ALLOWED",
    publicMintEnabled: mainnetMintAvailable,
    updatedAt: bootstrap ? Date.now() : null,
  };

  return {
    projectContractsMissing,
    environment,
    environmentLabel,
    environmentNote: deploymentError ?
      "The connected deployment is provisional or does not match Elysium. Reads and transactions remain unavailable until its identity is verified." : bootstrapError ?
      "Connected application mode. Backend unavailable; no demo data is substituted." :
      environment === "LOCAL" || environment === "FORK" ?
        "TEST ONLY — local transactions, not production" :
          `${mainnetMintAvailable ? "Minting is available under the current verified configuration." : "Minting is unavailable under the current configuration."} ${
            redeemAvailable ? "Redemption is available to the current NFT owner after a fresh review." : "Redemption is unavailable under the current configuration."} ${
            finalizeAvailable ? "Finalization is available after its current-state checks." : "Finalization is unavailable under the current configuration."} ${permissions.mining.state === "ALLOWED" ?
            "Mining is enabled by the backend; a verified proof does not authorize minting." : "Mining is disabled by the backend."}`,
    walletTransactionsEnabled: permissions.redeem.state === "ALLOWED",
    netBuy: {
      nativeSymbol: nativeSymbol(displayChainId),
      ...(reads.netBuyUpdates ? { updates: reads.netBuyUpdates } : {}),
      tierDisplay: tierDisplayView(reads.netBuy),
      ...(reads.netBuy?.netBuyThresholds ? { netBuyThresholds: reads.netBuy.netBuyThresholds } : {}),
      loading: reads.status.netBuy.loading && reads.netBuy === null,
      // Cached amounts are display-only; eligibility continues to use the current read.
      netBuyEth: (bootstrap?.sourceChainId === ELYSIUM.chainId
        ? (reads.netBuy ?? reads.netBuyDisplayData)?.netBuyHype
        : (reads.netBuy ?? reads.netBuyDisplayData)?.netBuyEth) ?? null,
      netBuyUsd: (reads.netBuy ?? reads.netBuyDisplayData)?.netBuyUsd ?? null,
      netBuyScope: reads.netBuy?.netBuyScope ?? null,
      eligibilityAvailable: reads.netBuy?.eligibilityAvailable ?? false,
      eligibleTiers: reads.netBuy?.eligibleTiers ?? null,
      maxTierUsd: reads.netBuy?.maxTierUsd ?? null,
      blockingReasons: reads.netBuy?.blockingReasons ?? (sourceTokenMissing ? ["TOKEN_NOT_LAUNCHED"] :
        reads.status.netBuy.error ? [reads.status.netBuy.error] : !account ? ["WALLET_NOT_CONNECTED"] : []),
      asOfBlock: reads.netBuy?.asOfBlock ?? null,
      asOfBlockHash: reads.netBuy?.asOfBlockHash ?? null,
      priceSource: reads.netBuy?.priceSource ?? null,
      priceTimestamp: reads.netBuy?.ethUsdTimestamp ?? null,
    },
    rewardPool: {
      nativeSymbol: nativeSymbol(displayChainId),
      loading: reads.status.pool.loading && reads.pool === null,
      available: reads.pool?.poolAvailable === true && reads.pool.dataStatus === "CURRENT",
      availableWei: reads.pool?.poolAvailable && reads.pool.dataStatus === "CURRENT" ? reads.pool.availableRewardWei : null,
      availableEth: displayPool?.availableRewardWei != null ? formatEth(BigInt(displayPool.availableRewardWei), 6) : null,
      totalPaidEth: displayPool?.totalPaidWei != null ? formatEth(BigInt(displayPool.totalPaidWei), 6) : null,
      vaultAddress: (reads.pool ?? reads.poolDisplayData)?.vaultAddress ?? null,
      asOfBlock: reads.pool?.asOfBlock ?? null,
      blockingReasons: reads.pool?.blockingReasons ?? (sourceTokenMissing ? ["TOKEN_NOT_LAUNCHED"] :
        reads.status.pool.error ? [reads.status.pool.error] : []),
    },
    mintFee: {
      nativeSymbol: nativeSymbol(displayChainId),
      preparationAvailable: preparation.state === "ALLOWED",
      preparationReason: reads.status.fee.error ?? (preparation.reasons.join(", ") || null),
      ...(reads.fee?.providerCheckMode ? { providerCheckMode: reads.fee.providerCheckMode } : {}),
      cacheStale,
      cacheUpdatedAt: reads.fee?.cache?.updatedAt ?? null,
      loading: reads.status.fee.loading && reads.fee === null,
      available: feeDisplay !== null,
      cumulativeTotalMinted: feeDisplay?.cumulativeTotalMinted ?? null,
      powTotalMinted: feeDisplay?.powTotalMinted ?? null,
      powTotalMintedAsOfBlock: feeDisplay?.powTotalMintedAsOfBlock ?? null,
      currentFeeWei: feeDisplay?.mintFeeWei ?? null,
      currentFeeEth: feeDisplay?.mintFeeWei ? formatEth(BigInt(feeDisplay.mintFeeWei), 6) : null,
      ...(feeDisplay?.tierMintFeesWei ? { tierMintFeesWei: feeDisplay.tierMintFeesWei } : {}),
      ...(feeDisplay?.nextStepTierMintFeesWei ? { nextStepTierMintFeesWei: feeDisplay.nextStepTierMintFeesWei } : {}),
      ...(feeDisplay?.successfulMintsUntilNextFeeStep !== undefined ?
        { successfulMintsUntilNextFeeStep: feeDisplay.successfulMintsUntilNextFeeStep } : {}),
      ...(feeDisplay?.mintFeeStepSize ? { mintFeeStepSize: feeDisplay.mintFeeStepSize } : {}),
      collectionAddress: feeDisplay?.contractAddress ?? bootstrap?.contracts.collectionAddress ?? null,
      blockNumber: feeDisplay?.blockNumber ?? null,
      mintEnabled: submission.state === "ALLOWED",
      testOnly: Boolean(bootstrap?.capabilities.localTestOnlyMintTransactionEnabled),
      reason: feeIssue ?? (cacheStale ? reads.fee?.cache?.errorCode ?? "MINT_DISPLAY_CACHE_STALE" : feeDisplay ? null : reads.status.fee.error ?? (
        projectContractsMissing ? "CONTRACT_NOT_CONFIGURED" :
        reads.fee?.reason ?? "MINT_STATE_UNAVAILABLE")),
    },
    rewardPolicy: {
      loading: reads.status.policy.loading && reads.policy === null,
      available: reads.policy !== null,
      policyVersion: reads.policy?.version ?? null,
      policyHash: reads.policy?.policyHash ?? null,
      tiers: reads.policy?.tiers.map((tier) => ({
        tierUsd: tier.tierUsd,
        rewardDenominator: reads.policy?.version === "2" ? 1_000_000 : 10_000,
        minRate: "rangePpm" in tier ? tier.rangePpm.min : tier.rangeBps.min,
        maxRate: "rangePpm" in tier ? tier.rangePpm.max : tier.rangeBps.max,
        bands: tier.bands.map((band) => ({ minRate: band.min, maxRate: band.max, weightBps: band.weightBps })),
      })) ?? null,
      blockingReason: reads.policy ? null : reads.status.policy.error ?? "REWARD_POLICY_UNAVAILABLE",
    },
    runtime,
  };
}

function mintFeeDisplayIssue(bootstrap: BootstrapResponse | null, fee: LiveReads["fee"]): string | null {
  if (!fee?.available) return null;
  // The fee DTO has no chain field. Bind its collection to the admitted
  // bootstrap's execution deployment rather than inventing a backend field.
  if (!bootstrap || !bootstrap.contracts.collectionAddress || !bootstrap.contracts.mintChainId ||
    bootstrap.contracts.mintChainId !== bootstrap.deployment.executionChainId ||
    !sameIdentity(fee.contractAddress, bootstrap.contracts.collectionAddress) ||
    bootstrap.sourceChainId === ELYSIUM.chainId && elysiumBootstrapReasons(bootstrap).length > 0) {
    return "MINT_FEE_DEPLOYMENT_MISMATCH";
  }
  if (bootstrap.sourceChainId === ELYSIUM.chainId) {
    // V2 prices are tier-specific backend observations, not the old linear fee formula.
    return fee.rewardPolicyVersion === ELYSIUM.policyVersion && fee.cumulativeTotalMinted !== null && fee.mintFeeWei !== null &&
      fee.rewardPolicyHash === ELYSIUM.policyHash && fee.mintFeeStepSize === 200 &&
      fee.tierMintFeesWei?.["10"] === fee.mintFeeWei ? null : "MINT_FEE_POLICY_MISMATCH";
  }
  if (fee.cumulativeTotalMinted === null || fee.mintFeeWei === null) {
    return "MINT_FEE_POLICY_MISMATCH";
  }
  // Legacy deployments retain their authoritative wallet-specific pricing.
  return null;
}
